import React from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ClipboardDocumentListIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";

import AudioRecorder from "../../utilities/AudioRecorder";
import Transcribe from "./Transcribe";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

// util (kept here in case you later want to format display dates)
const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toISOString().slice(0, 10); // yyyy-mm-dd for <input type="date">
  } catch {
    return "";
  }
};

const IconButton = ({ label, title, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative group rounded-xl px-3 py-2 text-white bg-sky-800 hover:bg-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 ${className}`}
    aria-label={label}
    title={title || ""}
  >
    {children}
    <span
      role="tooltip"
      className="
        pointer-events-none
        absolute -top-10 left-1/2 -translate-x-1/2
        whitespace-nowrap rounded bg-slate-700 p-2 text-sm text-white shadow
        opacity-0 translate-y-1
        transition-opacity transition-transform duration-150
        group-hover:opacity-100 group-hover:translate-y-0
        group-focus-visible:opacity-100 group-focus-visible:translate-y-0
      "
    >
      {label}
    </span>
  </button>
);

class HealthAssessment extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isEditing: false,

      // AI tools / Smart Paste state
      smartPasteOpen: false,
      smartPasteText: "",
      smartPasteError: "",
      smartPasteBusy: false,
      creditsBalance: null,
      // tab view
      activeTab: "Form"
    };
  }

  // ------ edit + propagate to parent ------

  toggleEdit = () => {
    this.setState((prev) => {
      const wasEditing = prev.isEditing;
      const nextVal = !wasEditing;

      // when turning editing OFF (Save), ask parent to persist
      if (wasEditing && typeof this.props.onSaveDraft === "function") {
        this.props.onSaveDraft();
      }

      return { isEditing: nextVal };
    });
  };

  handleFieldChange = (key, value) => {
    const current = this.props.value || {};
    const health = current.health || {};

    const updatedHealth = {
      ...health,
      [key]: value,
    };

    const nextValue = {
      ...current,
      health: updatedHealth,
    };

    if (typeof this.props.onChange === "function") {
      this.props.onChange(nextValue);
    }
  };

  // --- change tab ---
  handleTabChange = (tab) => {
    this.setState({ activeTab: tab });
  };

  // ---- tools ----
  openConsultationNote = () => {
    toast.info("Consultation note not implemented yet");
  };

  openSmartPaste = () => {
    this.setState({ smartPasteOpen: true, smartPasteText: "", smartPasteError: "" });
  };

  closeSmartPaste = () => {
    if (this.state.smartPasteBusy) return;
    this.setState({ smartPasteOpen: false, smartPasteText: "", smartPasteError: "" });
  };

  // called by AudioRecorder
  handleTranscription = (data) => {
    const transcription = data.transcription || "";
    const creditUsed = data.output?.amount;
    const newBalance = data.output?.newBalance;
    const reason = data.output?.reason;

    if (creditUsed && reason) {
      toast.info(`${creditUsed} credits was used to ${reason}`, {
        autoClose: 1500,
      });
    }

    this.setState(
      {
        creditsBalance: newBalance ?? this.state.creditsBalance,
        smartPasteText: transcription,
      },
      () => {
        this.toastId = toast.loading("Filling up health assessment...");
        this.handleTextProcessing(this.toastId);
      }
    );
  };

  handleTextProcessing = async (toastId) => {
    const { smartPasteText } = this.state;
    if (!smartPasteText.trim()) {
      this.setState({ smartPasteError: "Please paste or type something first." });
      return;
    }

    this.setState({ smartPasteBusy: true, smartPasteError: "" });

    const currentData = this.props.value; 

    try {
      const res = await axios.post(
        `${EndPoint}/careplan/healthassessment`,
        {
          transcription: smartPasteText,
          initData: currentData,
        },
        { withCredentials: true }
      );

      const payload = res?.data || {};
      const ai = JSON.parse(payload?.respond || "{}");

      const current = this.props.value || {};
      const prevHealth = current.health || {};

      const updatedHealth = {
        ...prevHealth,
        // Core HA fields from AI
        substance: ai.substance,
        medicationArrangement: ai.medicationArrangement,
        vision: ai.vision,
        hearing: ai.hearing,
        fallAndHomeSafety: ai.fallAndHomeSafety,
        skin: ai.skin,
        feet: ai.feet,
        nutrition: ai.nutrition,
        oralHealth: ai.oralHealth,
        cognition: ai.cognition,
        mentalHealth: ai.mentalHealth,
        urineContinence: ai.urineContinence,
        faecalContinence: ai.faecalContinence,
        mobility: ai.mobility,
        activity: ai.activity,
        social: ai.social,
        housing: ai.housing,
        legal: ai.legal,
        finance: ai.finance,
        driving: ai.driving,
        advancedCareDirective: ai.advancedCareDirective,
        services: ai.services,
        examination: ai.examination,
        surveillance: ai.surveillance,
        // keep date exactly what user already has
        date: prevHealth.date || "",
      };

      const nextValue = {
        ...current,
        health: updatedHealth,
      };

      if (typeof this.props.onChange === "function") {
        this.props.onChange(nextValue);
      }

      // credits (same pattern as MHCP)
      if (payload?.output) {
        const creditUsed = payload.output.amount;
        const newBalance = payload.output.newBalance;
        const reason = payload.output.reason;
        toast.info(`${creditUsed} credits was used to ${reason}`, {
          autoClose: 1500,
        });
        this.props.onCredits(newBalance);
      }

      this.setState({
        smartPasteBusy: false,
        smartPasteOpen: false,
        smartPasteText: "",
      });

      toast.update(toastId, {
        render: "Health assessment updated",
        type: "success",
        isLoading: false,
        autoClose: 1100,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        smartPasteBusy: false,
        smartPasteError:
          err?.response?.data?.error || err?.message || "Request failed",
      });
      toast.update(toastId, {
        render: "Error updating health assessment",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  // -------- Word / Clipboard export --------

  // getHealthWordHTML = () => {
  //   const { health = {} } = this.props.value || {};

  //   const esc = (x) =>
  //     String(x ?? "")
  //       .replace(/&/g, "&amp;")
  //       .replace(/</g, "&lt;")
  //       .replace(/>/g, "&gt;");

  //   const br = (x) =>
  //     esc(x)
  //       .replace(/\r?\n/g, "<br/>");

  //   const base =
  //     'font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif; font-size:8pt; line-height:1.3; color:#111; ' +
  //     'mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';

  //   const h2Style = {
  //     fontSize: "9pt",
  //     fontWeight: 700,
  //     margin: "6pt 0",
  //     padding: 0,
  //   };

  //   const h3Style = {
  //     fontSize: "8pt",
  //     fontWeight: 600,
  //     margin: "6pt 0 2pt",
  //     padding: 0,
  //   };

  //   const bodyTextStyle = {
  //     fontSize: "8pt",
  //     lineHeight: "1.3",
  //   };


  //   return `
  //     <!doctype html>
  //     <html>
  //     <head><meta charset="utf-8"></head>
  //     <body>
  //       <font size="1">
  //         <div style="${base}">
  //           <h2 style={h2Style} >Health Assessment</h2 style={h2Style} >

  //           <h3 style={h3Style} >Date</h3>
  //           <div style = {bodyTextStyle} >${br(health.date || "")}</div>

  //           <h3 style={h3Style} >Medication arrangement</h3>
  //           <div style = {bodyTextStyle} >${br(health.medicationArrangement || "")}</div>

  //           <h3 style={h3Style} >Substance use</h3>
  //           <div style = {bodyTextStyle} >${br(health.substance || "")}</div>

  //           <h3 style={h3Style} >Vision</h3>
  //           <div style = {bodyTextStyle} >${br(health.vision || "")}</div>

  //           <h3 style={h3Style} >Hearing</h3>
  //           <div style = {bodyTextStyle} >${br(health.hearing || "")}</div>

  //           <h3 style={h3Style} >Falls &amp; home safety</h3>
  //           <div style = {bodyTextStyle} >${br(health.fallAndHomeSafety || "")}</div>

  //           <h3 style={h3Style} >Skin</h3>
  //           <div style = {bodyTextStyle} >${br(health.skin || "")}</div>

  //           <h3 style={h3Style} >Feet</h3>
  //           <div style = {bodyTextStyle} >${br(health.feet || "")}</div>

  //           <h3 style={h3Style} >Nutrition</h3>
  //           <div style = {bodyTextStyle} >${br(health.nutrition || "")}</div>

  //           <h3 style={h3Style} >Oral health</h3>
  //           <div style = {bodyTextStyle} >${br(health.oralHealth || "")}</div>

  //           <h3 style={h3Style} >Cognition</h3>
  //           <div style = {bodyTextStyle} >${br(health.cognition || "")}</div>

  //           <h3 style={h3Style} >Mental health</h3>
  //           <div style = {bodyTextStyle} >${br(health.mentalHealth || "")}</div>

  //           <h3 style={h3Style} >Urine continence</h3>
  //           <div style = {bodyTextStyle} >${br(health.urineContinence || "")}</div>

  //           <h3 style={h3Style} >Faecal continence</h3>
  //           <div style = {bodyTextStyle} >${br(health.faecalContinence || "")}</div>

  //           <h3 style={h3Style} >Mobility</h3>
  //           <div style = {bodyTextStyle} >${br(health.mobility || "")}</div>

  //           <h3 style={h3Style} >Activity / exercise</h3>
  //           <div style = {bodyTextStyle} >${br(health.activity || "")}</div>

  //           <h3 style={h3Style} >Social</h3>
  //           <div style = {bodyTextStyle} >${br(health.social || "")}</div>

  //           <h3 style={h3Style} >Housing</h3>
  //           <div style = {bodyTextStyle} >${br(health.housing || "")}</div>

  //           <h3 style={h3Style} >Legal</h3>
  //           <div style = {bodyTextStyle} >${br(health.legal || "")}</div>

  //           <h3 style={h3Style} >Finance</h3>
  //           <div style = {bodyTextStyle} >${br(health.finance || "")}</div>

  //           <h3 style={h3Style} >Driving</h3>
  //           <div style = {bodyTextStyle} >${br(health.driving || "")}</div>

  //           <h3 style={h3Style} >Advance care directive</h3>
  //           <div style = {bodyTextStyle} >${br(health.advancedCareDirective || "")}</div>

  //           <h3 style={h3Style} >Services / supports</h3>
  //           <div style = {bodyTextStyle} >${br(health.services || "")}</div>

  //           <h3 style={h3Style} >Examination summary</h3>
  //           <div style = {bodyTextStyle} >${br(health.examination || "")}</div>

  //           <h3 style={h3Style} >Screening / surveillance</h3>
  //           <div style = {bodyTextStyle} >${br(health.surveillance || "")}</div>
  //         </div>
  //       </font>
  //     </body>
  //     </html>`;
  // };

  getHealthWordHTML = () => {
    const s = this.state;
    const { health = {} } = this.props.value || {};

    const esc = (x) =>
      String(x ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const br = (x) => esc(x).replace(/\r?\n/g, "<br/>");

    const base =
      "font-family:Arial, Helvetica, sans-serif; font-size:8pt; line-height:1.2; color:#111; " +
      "mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;";

    const sectionHeaderStyle =
      "margin:14pt 0 0 0; padding:9pt 10pt; background:#075985; color:#fff; font-weight:700; " +
      "font-size:8pt; line-height:1.2; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; border:0;";

    const tableStyle =
      "border-collapse:collapse; table-layout:fixed; width:100%; margin:0 0 18pt 0; " +
      "mso-table-lspace:0pt; mso-table-rspace:0pt;";

    const compactLabelStyle =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0;" +
    "padding:3pt 8pt; vertical-align:middle; font-weight:600; background:#f9fafb;" +
    "font-size:8pt; line-height:1.1; width:22%;";

  const compactValueStyleLeft =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:1px solid #d1d5db;" +
    "padding:3pt 8pt; vertical-align:middle; background:#fff;" +
    "font-size:8pt; line-height:1.1; width:28%;";

  const compactValueStyleRight =
    "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0;" +
    "padding:3pt 8pt; vertical-align:middle; background:#fff;" +
    "font-size:8pt; line-height:1.1; width:28%;";

    const label2ColStyle =
      "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
      "padding:8pt 10pt; vertical-align:top; font-weight:600; background:#f9fafb; " +
      "font-size:8pt; width:240px; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const value2ColStyle =
      "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
      "padding:8pt 10pt; vertical-align:top; background:#fff; " +
      "font-size:8pt; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const fourColLabelLeftStyle =
      "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
      "padding:8pt 10pt; vertical-align:top; font-weight:600; background:#f9fafb; " +
      "font-size:8pt; width:22%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const fourColValueLeftStyle =
      "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:1px solid #d1d5db; " +
      "padding:8pt 10pt; vertical-align:top; background:#fff; " +
      "font-size:8pt; width:28%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const fourColLabelRightStyle =
      "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
      "padding:8pt 10pt; vertical-align:top; font-weight:600; background:#f9fafb; " +
      "font-size:8pt; width:22%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const fourColValueRightStyle =
      "border-top:1px solid #d1d5db; border-bottom:1px solid #d1d5db; border-left:0; border-right:0; " +
      "padding:8pt 10pt; vertical-align:top; background:#fff; " +
      "font-size:8pt; width:28%; word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;";

    const sectionHeader = (title) =>
      `<div style="${sectionHeaderStyle}">${esc(title)}</div>`;

    const twoColRow = (label, value, fallback = "") => `
      <tr>
        <td style="${label2ColStyle}">${esc(label)}</td>
        <td style="${value2ColStyle}">${value && String(value).trim() ? value : br(fallback)}</td>
      </tr>
    `;

    const fourColCompactRow = (
    label1,
    value1,
    label2,
    value2,
    options = {}
  ) => {
    const { secondEmpty = false } = options;

    if (secondEmpty) {
      return `
        <tr>
          <td style="${compactLabelStyle}">${esc(label1 || "")}</td>
          <td style="${compactValueStyleLeft}">${value1 || ""}</td>
          <td style="${compactLabelStyle}"></td>
          <td style="${compactValueStyleRight}"></td>
        </tr>
      `;
    }

    return `
      <tr>
        <td style="${compactLabelStyle}">${esc(label1 || "")}</td>
        <td style="${compactValueStyleLeft}">${value1 || ""}</td>
        <td style="${compactLabelStyle}">${esc(label2 || "")}</td>
        <td style="${compactValueStyleRight}">${value2 || ""}</td>
      </tr>
    `;
  };

    const fourColRow = (
      label1,
      value1,
      label2,
      value2,
      options = {}
    ) => {
      const { secondEmpty = false } = options;

      if (secondEmpty) {
        return `
          <tr>
            <td style="${fourColLabelLeftStyle}">${esc(label1 || "")}</td>
            <td style="${fourColValueLeftStyle}">${value1 || ""}</td>
            <td style="${fourColLabelRightStyle}"></td>
            <td style="${fourColValueRightStyle}"></td>
          </tr>
        `;
      }

      return `
        <tr>
          <td style="${fourColLabelLeftStyle}">${esc(label1 || "")}</td>
          <td style="${fourColValueLeftStyle}">${value1 || ""}</td>
          <td style="${fourColLabelRightStyle}">${esc(label2 || "")}</td>
          <td style="${fourColValueRightStyle}">${value2 || ""}</td>
        </tr>
      `;
    };

    const patientAddress = [s.ptStreet, s.ptCity, s.ptPostcode, s.ptState]
      .filter(Boolean)
      .join(" ");

    return `
  <!doctype html>
  <html>
  <head>
    <meta charset="utf-8" />
  </head>
  <body style="font-family:Arial, Helvetica, sans-serif;">
    <div style="${base}">
      ${
        s.practiceLetterhead
          ? `<div style="font-size:16pt; font-weight:700; text-align:center; white-space:pre-wrap; margin-bottom:6pt;">${br(
              s.practiceLetterhead
            )}</div>`
          : ""
      }

      <div style="text-align:center; font-size:11pt; font-weight:700; margin:2pt 0;">
        Health Assessment
      </div>
      
      <div style="text-align:center; font-size:9pt; font-weight:400; margin:2pt;">
        Item 703/705/707/715
      </div>

      ${sectionHeader("Contact and Demographic Details")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${fourColCompactRow("GP name", br(s.gpName || ""), "GP phone", br(s.gpPhone || ""))}
        ${fourColCompactRow(
          "Practice name",
          br(s.practiceName || ""),
          "Provider number",
          br(s.providerNumber || "")
        )}
        ${fourColCompactRow(
          "Practice address",
          br(s.practiceAddress || ""),
          "",
          "",
          { secondEmpty: true }
        )}
        ${fourColCompactRow(
          "Patient name",
          br(s.ptFullName || ""),
          "Date of birth",
          br(typeof formatDate === "function" ? formatDate(s.ptDoB) : s.ptDoB || "")
        )}
        ${fourColCompactRow(
          "Patient address",
          br(patientAddress),
          "Phone",
          br(s.ptPhoneMob || s.ptPhoneH || "")
        )}
      </table>

      ${sectionHeader("Health Assessment")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${fourColRow("Date", br(health.date || ""), "Substance use", br(health.substance || ""))}
        ${fourColRow(
          "Medication arrangement",
          br(health.medicationArrangement || ""),
          "Vision",
          br(health.vision || "")
        )}
        ${fourColRow(
          "Hearing",
          br(health.hearing || ""),
          "Falls & home safety",
          br(health.fallAndHomeSafety || "")
        )}
        ${fourColRow("Skin", br(health.skin || ""), "Feet", br(health.feet || ""))}
        ${fourColRow(
          "Nutrition",
          br(health.nutrition || ""),
          "Oral health",
          br(health.oralHealth || "")
        )}
        ${fourColRow(
          "Cognition",
          br(health.cognition || ""),
          "Mental health",
          br(health.mentalHealth || "")
        )}
        ${fourColRow(
          "Urine continence",
          br(health.urineContinence || ""),
          "Faecal continence",
          br(health.faecalContinence || "")
        )}
        ${fourColRow(
          "Mobility",
          br(health.mobility || ""),
          "Activity / exercise",
          br(health.activity || "")
        )}
        ${fourColRow(
          "Social",
          br(health.social || ""),
          "Housing",
          br(health.housing || "")
        )}
        ${fourColRow(
          "Legal",
          br(health.legal || ""),
          "Finance",
          br(health.finance || "")
        )}
        ${fourColRow(
          "Driving",
          br(health.driving || ""),
          "Advance care directive",
          br(health.advancedCareDirective || "")
        )}
        ${fourColRow(
          "Services / supports",
          br(health.services || ""),
          "Examination summary",
          br(health.examination || "")
        )}
        ${fourColRow(
          "Screening / surveillance",
          br(health.surveillance || ""),
          "",
          "",
          { secondEmpty: true }
        )}
      </table>

      ${sectionHeader("Completion")}
      <table border="0" cellspacing="0" cellpadding="0" style="${tableStyle}">
        ${fourColRow(
          "Date completed",
          br(typeof formatDate === "function" ? formatDate(s.todaysDate) : s.todaysDate || ""),
          "",
          "",
          { secondEmpty: true }
        )}
        <tr>
          <td style="${fourColLabelLeftStyle}">GP signature</td>
          <td style="${fourColValueLeftStyle}">
            ${
              s.gpSignatureDataUrl
                ? `<img src="${esc(
                    s.gpSignatureDataUrl
                  )}" alt="Signature" style="display:block; width:80pt; height:auto; margin:0; border:0;" />`
                : ""
            }
          </td>
          <td style="${fourColLabelRightStyle}">GP name</td>
          <td style="${fourColValueRightStyle}">${br(s.gpName || "")}</td>
        </tr>
      </table>
    </div>
  </body>
  </html>`;
  };

  copyHealthToClipboard = async () => {
    const htmlDoc = this.getHealthWordHTML();
    const plain = document.getElementById("health-print-root")?.innerText || "";

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([htmlDoc], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
        window.alert(
          "Copied Health Assessment with formatting. Paste into Word with Ctrl/Cmd+V."
        );
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plain);
        window.alert(
          "Copied Health Assessment as plain text (browser doesn’t allow HTML clipboard)."
        );
        return;
      }

      const ta = document.createElement("textarea");
      ta.value = plain;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      window.alert("Copied Health Assessment as plain text.");
    } catch (e) {
      console.error(e);
      window.alert("Copy failed (clipboard permissions?).");
    }
  };

  // -------- Render pieces --------

  renderSmartPasteModal() {
    if (!this.state.smartPasteOpen) return null;

    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) this.closeSmartPaste();
        }}
      >
        <div
          className="bg-white rounded-2xl shadow-xl p-4 flex flex-col"
          style={{ width: 800, height: 800 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="mb-2">
            <h3 className="text-lg font-semibold">Smart Paste</h3>
            <p className="text-xs text-gray-500">
              Paste unstructured notes here. On submit, we’ll send it to your AI endpoint.
            </p>
          </div>

          <textarea
            className="flex-1 w-full resize-none rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
            placeholder="Paste or type notes…"
            value={this.state.smartPasteText}
            onChange={(e) =>
              this.setState({
                smartPasteText: e.target.value,
                smartPasteError: "",
              })
            }
          />

          {this.state.smartPasteError && (
            <div className="mt-2 text-sm text-red-600">
              {this.state.smartPasteError}
            </div>
          )}

          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
              onClick={this.closeSmartPaste}
              disabled={this.state.smartPasteBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`rounded-xl px-3 py-2 text-sm text-white ${
                this.state.smartPasteBusy
                  ? "bg-gray-500"
                  : "bg-black hover:bg-black/90"
              }`}
              onClick={() => {
                this.toastId = toast.loading("Processing...");
                this.handleTextProcessing(this.toastId);
              }}
              disabled={this.state.smartPasteBusy}
            >
              {this.state.smartPasteBusy ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderTools() {
    const currentCredits = this.props.currentCredits;
    const isDisabled = currentCredits < 10;

    return (
      <div className="mb-4 flex items-center justify-center space-x-4 ">
        <div
          className={`
            pl-4 flex gap-4 items-center justify-center relative
            ${isDisabled ? "opacity-40 pointer-events-none" : ""}
          `}
          aria-disabled={isDisabled}
          title={
            isDisabled
              ? "You need at least 10 credits to use AI tools"
              : ""
          }
        >
          <IconButton
            label="AI Write or Paste"
            onClick={this.openSmartPaste}
          >
            <ClipboardDocumentListIcon className="w-8 h-8" />
          </IconButton>
          <div className=''>
          <Transcribe onInputUpdate={this.handleTranscription} />
          </div>
          <div className="absolute left-14 -bottom-4 text-xs text-gray-500">
            AI Tools
          </div>
        </div>
      </div>
    );
  }

  // renderHealthPreview() {
  //   const { health = {} } = this.props.value || {};

  //   const fields = [
  //     { label: "Date", key: "date" },
  //     { label: "Substance use", key: "substance" },
  //     { label: "Medication arrangement", key: "medicationArrangement" },
  //     { label: "Vision", key: "vision" },
  //     { label: "Hearing", key: "hearing" },
  //     { label: "Falls & home safety", key: "fallAndHomeSafety" },
  //     { label: "Skin", key: "skin" },
  //     { label: "Feet", key: "feet" },
  //     { label: "Nutrition", key: "nutrition" },
  //     { label: "Oral health", key: "oralHealth" },
  //     { label: "Cognition", key: "cognition" },
  //     { label: "Mental health", key: "mentalHealth" },
  //     { label: "Urine continence", key: "urineContinence" },
  //     { label: "Faecal continence", key: "faecalContinence" },
  //     { label: "Mobility", key: "mobility" },
  //     { label: "Activity / exercise", key: "activity" },
  //     { label: "Social", key: "social" },
  //     { label: "Housing", key: "housing" },
  //     { label: "Legal", key: "legal" },
  //     { label: "Finance", key: "finance" },
  //     { label: "Driving", key: "driving" },
  //     { label: "Advance care directive", key: "advancedCareDirective" },
  //     { label: "Services / supports", key: "services" },
  //     { label: "Examination summary", key: "examination" },
  //     { label: "Screening / surveillance", key: "surveillance" },
  //   ];


  //   const subscription = this.props.hasSubscription

  //   const {hasSubscription} = this.state;
  //   if (hasSubscription === null) {
  //     return null;
  //   }

  //   return (
  //     <div
  //       id="health-print-root"
  //       className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm print:border-0 print:shadow-none mt-4"
  //     >
  //    <button
  //       className={`
  //         bg-sky-700 text-white px-3 py-2 rounded-md text-md mb-3
  //         transition
  //         ${
  //           subscription === false
  //             ? "opacity-40 cursor-not-allowed"
  //             : "hover:bg-sky-800"
  //         }
  //       `}
  //       disabled={this.props.hasSubcription === false}
  //       onClick={
  //         subscription === false
  //           ? undefined
  //           : this.copyHealthToClipboard
  //       }
  //     >
  //       Copy Health Assessment
  //     </button>


  //       <div className="prose max-w-none relative text-sm text-black">
  //         <h2 className="mt-2 text-xl font-bold text-center">
  //           Health Assessment
  //         </h2>
  //            {!subscription && (
  //           <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
  //             <div className="px-12 py-20 border-4 border-red-600 text-red-600 text-[10rem] font-extrabold uppercase tracking-[0.2em] opacity-40 rotate-[-20deg]">
  //               Demo Only
  //             </div>
  //           </div>
  //         )}

  //         <div className="mt-4 space-y-2">
  //           {fields.map(({ label, key }) => {
  //             const value = health[key];
  //             if (!value || String(value).trim() === "") return null;

  //             return (
  //               <div key={key} className="text-sm">
  //                 <strong>{label}: </strong>
  //                 <span className="whitespace-pre-wrap">{value}</span>
  //               </div>
  //             );
  //           })}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // ---- main form ----
  
  
  renderHealthPreview() {
    const s = this.state;
    const { health = {} } = this.props.value || {};
    const subscription = this.props.hasSubscription;

    if (subscription === null) {
      return null;
    }

    const renderCellContent = (val, fallback = "") => {
      if (
        !val ||
        (Array.isArray(val) && val.length === 0) ||
        (typeof val === "string" && !val.trim())
      ) {
        return <div className="whitespace-pre-wrap">{fallback}</div>;
      }

      if (Array.isArray(val)) {
        return val.map((item, idx) => (
          <div key={idx} className="whitespace-pre-wrap">
            {item}
          </div>
        ));
      }

      return <div className="whitespace-pre-wrap">{val}</div>;
    };

    const renderSectionHeader = (title) => (
      <div className="mt-6 bg-sky-800 text-white font-bold px-4 py-3 rounded-t-md">
        {title}
      </div>
    );

    const renderFourColRow = (
      label1,
      value1,
      label2,
      value2,
      options = {}
    ) => {
      const { secondEmpty = false } = options;

      if (secondEmpty) {
        return (
          <div className="grid grid-cols-4 border-b border-gray-200 last:border-b-0">
            <div className="bg-gray-50 px-4 py-3 font-semibold">
              {label1}
            </div>
            <div className="px-4 py-3 border-r border-gray-200">
              {renderCellContent(value1)}
            </div>
            <div className="px-4 py-3 bg-white"></div>
            <div className="px-4 py-3 bg-white"></div>
          </div>
        );
      }

      return (
        <div className="grid grid-cols-4 border-b border-gray-200 last:border-b-0">
          <div className="bg-gray-50 px-4 py-3 font-semibold">
            {label1}
          </div>
          <div className="px-4 py-3 border-r border-gray-200">
            {renderCellContent(value1)}
          </div>
          <div className="bg-gray-50 px-4 py-3 font-semibold">
            {label2}
          </div>
          <div className="px-4 py-3">
            {renderCellContent(value2)}
          </div>
        </div>
      );
    };

    const patientAddress = [s.ptStreet, s.ptCity, s.ptPostcode, s.ptState]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        id="health-print-root"
        className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm print:border-0 print:shadow-none mt-4 ${
          subscription === false ? "select-none" : ""
        }`}
      >
        <button
          className={`
            bg-sky-700 text-white px-3 py-2 rounded-md text-md mb-3
            transition
            ${
              subscription === false
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-sky-800"
            }
          `}
          disabled={this.props.hasSubscription === false}
          onClick={
            subscription === false
              ? undefined
              : this.copyHealthToClipboard
          }
        >
          Copy Health Assessment
        </button>

        <div className="max-w-none relative text-sm text-black">
          <h2 className="mt-2 text-xl font-bold text-center">
            Health Assessment
          </h2>

          {!subscription && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="px-12 py-20 border-4 border-red-600 text-red-600 text-[10rem] font-extrabold uppercase tracking-[0.2em] opacity-40 rotate-[-20deg]">
                Demo Only
              </div>
            </div>
          )}

          {renderSectionHeader("Contact and Demographic Details")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderFourColRow(
              "GP name",
              s.gpName || "",
              "GP phone",
              s.gpPhone || ""
            )}

            {renderFourColRow(
              "Practice name",
              s.practiceName || "",
              "Provider number",
              s.providerNumber || ""
            )}

            {renderFourColRow(
              "Practice address",
              s.practiceAddress || "",
              "",
              "",
              { secondEmpty: true }
            )}

            {renderFourColRow(
              "Patient name",
              s.ptFullName || "",
              "Date of birth",
              formatDate(s.ptDoB)
            )}

            {renderFourColRow(
              "Patient address",
              patientAddress,
              "Phone",
              s.ptPhoneMob || s.ptPhoneH || ""
            )}
          </div>

          {renderSectionHeader("Health Assessment")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderFourColRow(
              "Date",
              health.date || "",
              "Substance use",
              health.substance || ""
            )}

            {renderFourColRow(
              "Medication arrangement",
              health.medicationArrangement || "",
              "Vision",
              health.vision || ""
            )}

            {renderFourColRow(
              "Hearing",
              health.hearing || "",
              "Falls & home safety",
              health.fallAndHomeSafety || ""
            )}

            {renderFourColRow(
              "Skin",
              health.skin || "",
              "Feet",
              health.feet || ""
            )}

            {renderFourColRow(
              "Nutrition",
              health.nutrition || "",
              "Oral health",
              health.oralHealth || ""
            )}

            {renderFourColRow(
              "Cognition",
              health.cognition || "",
              "Mental health",
              health.mentalHealth || ""
            )}

            {renderFourColRow(
              "Urine continence",
              health.urineContinence || "",
              "Faecal continence",
              health.faecalContinence || ""
            )}

            {renderFourColRow(
              "Mobility",
              health.mobility || "",
              "Activity / exercise",
              health.activity || ""
            )}

            {renderFourColRow(
              "Social",
              health.social || "",
              "Housing",
              health.housing || ""
            )}

            {renderFourColRow(
              "Legal",
              health.legal || "",
              "Finance",
              health.finance || ""
            )}

            {renderFourColRow(
              "Driving",
              health.driving || "",
              "Advance care directive",
              health.advancedCareDirective || ""
            )}

            {renderFourColRow(
              "Services / supports",
              health.services || "",
              "Examination summary",
              health.examination || ""
            )}

            {renderFourColRow(
              "Screening / surveillance",
              health.surveillance || "",
              "",
              "",
              { secondEmpty: true }
            )}
          </div>

          {renderSectionHeader("Completion")}
          <div className="border border-t-0 border-gray-200 rounded-b-md overflow-hidden text-sm">
            {renderFourColRow(
              "Date completed",
              formatDate(s.todaysDate),
              "",
              "",
              { secondEmpty: true }
            )}

            <div className="grid grid-cols-4 border-b border-gray-200 last:border-b-0">
              <div className="bg-gray-50 px-4 py-3 font-semibold">
                GP signature
              </div>
              <div className="px-4 py-3 border-r border-gray-200">
                {s.gpSignatureDataUrl ? (
                  <img
                    src={s.gpSignatureDataUrl}
                    alt="GP signature"
                    className="h-16 object-contain"
                    style={{ imageRendering: "auto" }}
                  />
                ) : (
                  <span></span>
                )}
              </div>
              <div className="bg-gray-50 px-4 py-3 font-semibold">
                GP name
              </div>
              <div className="px-4 py-3">
                {s.gpName || ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  
  // renderForm() {
  //   const { isEditing } = this.state;
  //   const { health = {} } = this.props.value || {};

  //   const fields = [
  //     { label: "Substance use", key: "substance" },
  //     { label: "Medication arrangement", key: "medicationArrangement" },
  //     { label: "Vision", key: "vision" },
  //     { label: "Hearing", key: "hearing" },
  //     { label: "Falls & home safety", key: "fallAndHomeSafety" },
  //     { label: "Skin", key: "skin" },
  //     { label: "Feet", key: "feet" },
  //     { label: "Nutrition", key: "nutrition" },
  //     { label: "Oral health", key: "oralHealth" },
  //     { label: "Cognition", key: "cognition" },
  //     { label: "Mental health", key: "mentalHealth" },
  //     { label: "Urine continence", key: "urineContinence" },
  //     { label: "Faecal continence", key: "faecalContinence" },
  //     { label: "Mobility", key: "mobility" },
  //     { label: "Activity / exercise", key: "activity" },
  //     { label: "Social", key: "social" },
  //     { label: "Housing", key: "housing" },
  //     { label: "Legal", key: "legal" },
  //     { label: "Finance", key: "finance" },
  //     { label: "Driving", key: "driving" },
  //     { label: "Advance care directive", key: "advancedCareDirective" },
  //     { label: "Services / supports", key: "services" },
  //     { label: "Examination summary", key: "examination" },
  //     { label: "Screening / surveillance", key: "surveillance" },
  //   ];

  //   return (
  //     <div className="col-span-6 p-4 bg-slate-100 rounded shadow-md m-2">
  //       <div className="flex justify-between items-center">
  //         <h2 className="text-xl font-bold text-center">Health Assessment</h2>
  //         <button
  //           onClick={this.toggleEdit}
  //           className={`px-4 py-2 rounded text-white ${
  //             isEditing
  //               ? "bg-sky-600 hover:bg-sky-700"
  //               : "bg-yellow-500 hover:bg-yellow-600"
  //           }`}
  //         >
  //           {isEditing ? "Save" : "Edit"}
  //         </button>
  //       </div>

  //       {/* Date row */}
  //       <div className="mt-4 mb-4 max-w-xs">
  //         <h3 className="text-sm font-semibold">Date</h3>
  //         <input
  //           type="date"
  //           className="w-full text-black text-sm border-gray-200 rounded-md"
  //           value={health.date || ""}
  //           readOnly={!isEditing}
  //           disabled={!isEditing}
  //           onChange={(e) => this.handleFieldChange("date", e.target.value)}
  //         />
  //       </div>

  //       {/* Main form: 2 columns on md+, 1 column on xs */}
  //       <div className="mt-2 grid grid-cols-2 gap-4 xs:grid-cols-1">
  //         {fields.map((field) => (
  //           <div key={field.key}>
  //             <h3 className="text-sm font-semibold">{field.label}</h3>
  //             <textarea
  //               className="w-full h-32 resize-none text-black text-sm border border-gray-200 rounded-md"
  //               value={health[field.key] || ""}
  //               readOnly={!isEditing}
  //               disabled={!isEditing}
  //               onChange={(e) =>
  //                 this.handleFieldChange(field.key, e.target.value)
  //               }
  //             />
  //           </div>
  //         ))}
  //       </div>
  //     </div>
  //   );
  // }
renderForm() {
  const { isEditing } = this.state;
  const { health = {} } = this.props.value || {};

  const fields = [
    { label: "Substance use", key: "substance" },
    { label: "Medication arrangement", key: "medicationArrangement" },
    { label: "Vision", key: "vision" },
    { label: "Hearing", key: "hearing" },
    { label: "Falls & home safety", key: "fallAndHomeSafety" },
    { label: "Skin", key: "skin" },
    { label: "Feet", key: "feet" },
    { label: "Nutrition", key: "nutrition" },
    { label: "Oral health", key: "oralHealth" },
    { label: "Cognition", key: "cognition" },
    { label: "Mental health", key: "mentalHealth" },
    { label: "Urine continence", key: "urineContinence" },
    { label: "Faecal continence", key: "faecalContinence" },
    { label: "Mobility", key: "mobility" },
    { label: "Activity / exercise", key: "activity" },
    { label: "Social", key: "social" },
    { label: "Housing", key: "housing" },
    { label: "Legal", key: "legal" },
    { label: "Finance", key: "finance" },
    { label: "Driving", key: "driving" },
    { label: "Advance care directive", key: "advancedCareDirective" },
    { label: "Services / supports", key: "services" },
    { label: "Examination summary", key: "examination" },
    { label: "Screening / surveillance", key: "surveillance" },
  ];

  return (
    <div className="col-span-6 m-2 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-cyan-700 px-6 py-5 text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Health Assessment
            </h2>
            <p className="mt-1 text-sm text-sky-100">
              Review lifestyle, function, safety, supports and clinical concerns.
            </p>
          </div>

          <button
            onClick={this.toggleEdit}
            className={`inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow transition ${
              isEditing
                ? "bg-emerald-500 hover:bg-emerald-600"
                : "bg-amber-500 hover:bg-amber-600"
            }`}
          >
            {isEditing ? "Save Assessment" : "Edit Assessment"}
          </button>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        {/* Top info bar */}
        <div className="mb-6 rounded-2xl border border-sky-100 bg-sky-50/70 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-sky-900">
                Assessment Date
              </label>
              <input
                type="date"
                className={`w-full rounded-xl border px-3 py-2 text-sm text-black shadow-sm outline-none transition ${
                  isEditing
                    ? "border-sky-200 bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                    : "border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed"
                }`}
                value={health.date || ""}
                readOnly={!isEditing}
                disabled={!isEditing}
                onChange={(e) => this.handleFieldChange("date", e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <div className="rounded-xl bg-white/80 px-4 py-3 text-sm text-slate-600 border border-slate-200 w-full">
                Capture key concerns across physical health, cognition, mood,
                mobility, continence, social supports and preventive care.
              </div>
            </div>
          </div>
        </div>

        {/* Main form grid */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {fields.map((field, index) => (
            <div
              key={field.key}
              className="group rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    {field.label}
                  </h3>
                  <div className="mt-1 h-1 w-10 rounded-full bg-sky-500/70" />
                </div>
                <div className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500">
                  {String(index + 1).padStart(2, "0")}
                </div>
              </div>

              <textarea
                className={`w-full resize-none rounded-xl border px-3 py-3 text-sm text-black outline-none transition ${
                  isEditing
                    ? "h-32 border-slate-200 bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                    : "h-28 border-slate-200 bg-slate-100 text-slate-700"
                }`}
                placeholder={
                  isEditing ? `Enter ${field.label.toLowerCase()}...` : ""
                }
                value={health[field.key] || ""}
                readOnly={!isEditing}
                disabled={!isEditing}
                onChange={(e) =>
                  this.handleFieldChange(field.key, e.target.value)
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


  renderActiveTabContent = () => {
    const { activeTab } = this.state;
    if (activeTab === "Form") return this.renderForm();
    return this.renderHealthPreview();
  };

  renderTabs = () => {
    const { activeTab } = this.state;
    return (
      <div className="flex border-b mb-4">
        <button
          type="button"
          onClick={() => this.handleTabChange("Form")}
          className={
            "px-4 py-2 text-sm font-medium border-b-2 " +
            (activeTab === "Form"
              ? "border-sky-600 text-sky-700"
              : "border-transparent text-gray-500 hover:text-gray-700")
          }
        >
          Form
        </button>
        <button
          type="button"
          onClick={() => this.handleTabChange("Preview")}
          className={
            "px-4 py-2 text-sm font-medium border-b-2 " +
            (activeTab === "Preview"
              ? "border-sky-600 text-sky-700"
              : "border-transparent text-gray-500 hover:text-gray-700")
          }
        >
          Preview
        </button>
      </div>
    );
  };


  render() {

    return (
      <div
        className="col-span-6 grid-rows-4 xs:grid-cols-1 md:grid-cols-1"
        style={{
          gridTemplateRows:
            "minmax(12px, auto) minmax(12px, auto) minmax(320px, auto)",
        }}
      >
        {/* Title */}
        <div
          id="main"
          className="text-center col-span-6 text-3xl font-bold py-4 text-sky-900"
        >
          Health Assessment
        </div>

        {/* Tools */}
        {this.renderTools()}

        {/* Form */}
        {/* <div className="tabContent col-span-6">{this.renderForm()}</div> */}
        {this.renderTabs()}
        {this.renderActiveTabContent()}
        {this.renderSmartPasteModal()}
 
        <ToastContainer />
      </div>
    );
  }
}

export default HealthAssessment;
